import { Button, Flex, Heading, IconAddLine, List } from "@instructure/ui";
import { useTranslation } from "react-i18next";
import React, { createContext, useContext, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { AssessmentPlanService } from "../../services/AssessmentService";
import { CyclesService, CycleTypeService } from "../../services/CyclesService";
import { UserDetailContext } from "../../utils/Context";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { CycleTypePanel } from "./CycleTypes/CycleTypePanel";
import { CycleEditModal } from "./Cycles/CycleEditModal";
import CycleEntry from "./Cycles/CycleEntry";

export const CycleContext = createContext();

export const CyclesAndGoals = () => {
  const { t } = useTranslation();
  const userDetails = useContext(UserDetailContext);
  const showNotification = useNotification();
  const [loading, setLoading] = useState(true);
  const [cycles, setCycles] = useState([]);
  const [cycleTypes, setCycleTypes] = useState([]);
  const [usedCycles, setUsedCycles] = useState([]);

  useEffect(() => {
    async function fetchCycles() {
      try {
        const [cycles, cycleTypes, plans] = await Promise.all([
          CyclesService.getCycles(),
          CycleTypeService.getCycleTypes(),
          AssessmentPlanService.getLightAssessmentPlans(),
        ]);
        const sortedCycles = cycles
          .sort((a, b) => new Date(a.end_date) - new Date(b.end_date))
          .map((cycle) => ({ ...cycle, label: cycle.name }));
        setCycles(sortedCycles);

        setCycleTypes(
          cycleTypes.map((type) => ({ ...type, label: type.name }))
        );
        const planIds = plans.map((plan) => plan.cycle.id);
        setUsedCycles(planIds);
        setLoading(false);
      } catch {
        showNotification(
          t("notifications.general.fetchError", {
            obj: t("cyclesAndGoals.cycles"),
          }),
          ERROR
        );
      }
    }
    fetchCycles();
  }, [userDetails, showNotification]);

  return (
    <CycleContext.Provider
      value={{ cycles, setCycles, cycleTypes, setCycleTypes }}
    >
      <div
        style={{
          padding: "0.125rem",
          marginBottom: "15px",
          borderBottom: "1px solid #ccc",
        }}
      >
        <Flex gap="small">
          <Flex.Item shouldGrow>
            <Heading as="h2" level="h3">
              {t("cyclesAndGoals.heading")}
            </Heading>
          </Flex.Item>
          <Flex.Item>
            <AddCycleButton />
          </Flex.Item>
          <Flex.Item>
            <ManageCycleTypesButton />
          </Flex.Item>
        </Flex>
      </div>
      <div>
        {loading ? (
          <Skeleton count={3} style={{ marginBottom: 10, height: 152 }} />
        ) : (
          <List isUnstyled>
            {cycles.map((cycle) => (
              <List.Item key={cycle.id} margin="0 0 medium 0">
                <CycleEntry cycle={cycle} usedCycles={usedCycles} />
              </List.Item>
            ))}
          </List>
        )}
      </div>
    </CycleContext.Provider>
  );
};

const AddCycleButton = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        id="add-cycle-button"
        color="primary"
        renderIcon={IconAddLine}
        onClick={() => setOpen(true)}
      >
        {t("cyclesAndGoals.addButton")}
      </Button>
      <CycleEditModal open={open} setOpen={setOpen} />
    </>
  );
};

const ManageCycleTypesButton = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        id="manage-cycle-types-button"
        color="primary"
        onClick={() => setOpen(true)}
      >
        {t("cyclesAndGoals.cycleType.manageCycleTypes")}
      </Button>
      <CycleTypePanel open={open} setOpen={setOpen} />
    </>
  );
};
